import React, { Component } from 'react';
import autoBind from 'react-autobind';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import moment from 'moment';
import matchMedia from 'matchmediaquery';

import { UncontrolledTooltip } from 'reactstrap';
import { scroller } from 'react-scroll';

import {
  ensureDateMonth,
  compareByStartDateThenEndDate,
  minDateByYearAndMonth,
  maxDateByYearAndMonth,
  formatEntryDateLongYearShortMonth,
  formatEntryDateShortYearShortMonth,
  presentDateInYearAndMonth,
} from 'utils/date';

import themeConfig from 'ui/theme/config';
import styles from './career-timelines-viz.module.scss';
import Button from 'ui/common/button';
import InfoModal from './career-timelines-viz-info-modal';

const beltSizeInMonths =
  Number(process.env.REACT_APP_CAREER_TIMELINE_COLOR_TRANSITION_DURATION_IN_MONTHS) || 36;

const colors = ['yellow', 'orange', 'green', 'blue', 'purple', 'black'];
const grayColor = 'gray';

class CareerTimelinesViz extends Component {
  constructor(props) {
    super(props);

    autoBind(this);

    this.state = {
      hoveredEntryId: null,
      infoModalOpen: false,
    };
  }

  onSegmentMouseEnter(entryId) {
    this.setState({ hoveredEntryId: entryId });
  }

  onSegmentMouseLeave(entryId) {
    if (this.state.hoveredEntryId === entryId) this.setState({ hoveredEntryId: null });
  }

  onSegmentClick(entryId, entrySchemaType) {
    scroller.scrollTo(
      entrySchemaType === 'education'
        ? `education-entry-${entryId}`
        : `work-experience-entry-${entryId}`,
      { smooth: true }
    );
  }

  renderSegment(segmentIndex, beltIndex, offsetInMonths, sizeInMonths, entryClass, entry) {
    const { settings, careerSizeInMonths } = this.props;
    const entrySchemaType = entry && entry.meta && entry.meta.type;
    const left = 100 * offsetInMonths / careerSizeInMonths;
    const width = 100 * sizeInMonths / careerSizeInMonths;
    const colorClass =
      styles[
        `segment-${entrySchemaType === 'education'
          ? grayColor
          : colors[beltIndex >= colors.length ? colors.length - 1 : beltIndex]}`
      ];
    const elementId = `entry-${entry.id.toLowerCase()}-segment-${segmentIndex}`;

    const { intl: { locale } } = this.props;
    const localeData = moment.localeData(locale);
    const monthNames = localeData.monthsShort();

    const cssClass = classnames({
      [styles.segment]: true,
      [colorClass]: true,
      [entryClass]: true,
      [styles.segmentScaleOnHover]: settings.scaleOnHover && entry.id === this.state.hoveredEntryId,
      [styles.segmentHovered]: entry.id === this.state.hoveredEntryId,
    });

    return (
      <div
        key={`segment-${segmentIndex}`}
        className={cssClass}
        style={{ left: `${left}%`, width: `${width}%` }}
        onMouseEnter={() => this.onSegmentMouseEnter(entry.id, entrySchemaType)}
        onMouseLeave={() => this.onSegmentMouseLeave(entry.id, entrySchemaType)}
      >
        <div className={styles.segmentInner} id={elementId} />
        <UncontrolledTooltip target={elementId}>
          {entrySchemaType === 'education'
            ? entry.schoolName
            : [entry.title, entry.companyName].filter(Boolean).join(', ')}
          {((entry.startDate && entry.startDate.year && entry.startDate.month) ||
            (entry.currentlyWorking ||
              (entry.endDate && entry.endDate.year && entry.endDate.month))) && (
            <div className="text-muted">
              {[
                entry.startDate &&
                  `${monthNames[entry.startDate.month - 1]} ${entry.startDate.year}`,
                entry.currentlyWorking
                  ? 'Present'
                  : entry.endDate && `${monthNames[entry.endDate.month - 1]} ${entry.endDate.year}`,
              ]
                .filter(val => val)
                .join(' - ')}
            </div>
          )}
        </UncontrolledTooltip>
      </div>
    );
  }

  renderTimeline(timeline, timelineIndex) {
    const {
      careerStartDate,
      careerSizeInMonths,
      timelinesCount,
      settings: { timelineBreadthInRem, timelineGutterInRem, showScale },
    } = this.props;

    const segments = [];
    const tickMarkers = [];

    timeline.forEach((entry, entryIndex, entries) => {
      const entryOffsetInMonths = dateDiffInMonths(careerStartDate, entry.startDate);
      const entrySizeInMonths = dateDiffInMonths(entry.startDate, effectiveEndDate(entry));
      const isLastEntryInTimeline = entryIndex === entries.length - 1;

      const startPartialBeltIndex = Math.floor(entryOffsetInMonths / beltSizeInMonths);
      let startPartialBeltSizeInMonths =
        ((startPartialBeltIndex + 1) * beltSizeInMonths - entryOffsetInMonths) % beltSizeInMonths;
      if (startPartialBeltSizeInMonths > entrySizeInMonths) {
        startPartialBeltSizeInMonths = entrySizeInMonths;
      }

      const midBeltsStartIndex = Math.floor(
        (entryOffsetInMonths + startPartialBeltSizeInMonths) / beltSizeInMonths
      );
      const midBeltsCount = Math.floor(
        (entrySizeInMonths - startPartialBeltSizeInMonths) / beltSizeInMonths
      );

      const endPartialBeltIndex = midBeltsStartIndex + midBeltsCount;
      const endPartialBeltSizeInMonths =
        entrySizeInMonths - beltSizeInMonths * midBeltsCount - startPartialBeltSizeInMonths;

      const entrySegmentsCount =
        (startPartialBeltSizeInMonths > 0 ? 1 : 0) +
        midBeltsCount +
        (endPartialBeltSizeInMonths > 0 ? 1 : 0);
      let entrySegmentsIndex = 0;

      if (startPartialBeltSizeInMonths > 0) {
        segments.push(
          this.renderSegment(
            segments.length,
            startPartialBeltIndex,
            entryOffsetInMonths,
            startPartialBeltSizeInMonths,
            classnames({
              [styles.segmentEntryStart]: true,
              [styles.segmentEntryEnd]: entrySegmentsCount === 1,
              [styles.segmentEntryLast]: isLastEntryInTimeline,
            }),
            entry
          )
        );
        entrySegmentsIndex++;
      }

      for (
        let midBeltIndex = midBeltsStartIndex;
        midBeltIndex < midBeltsStartIndex + midBeltsCount;
        midBeltIndex++
      ) {
        segments.push(
          this.renderSegment(
            segments.length,
            midBeltIndex,
            midBeltIndex * beltSizeInMonths,
            beltSizeInMonths,
            classnames({
              [styles.segmentEntryStart]: entrySegmentsIndex === 0,
              [styles.segmentEntryMid]:
                entrySegmentsIndex > 0 && entrySegmentsIndex < entrySegmentsCount - 1,
              [styles.segmentEntryEnd]: entrySegmentsIndex === entrySegmentsCount - 1,
              [styles.segmentEntryLast]: isLastEntryInTimeline,
            }),
            entry
          )
        );
        entrySegmentsIndex++;
      }

      if (endPartialBeltSizeInMonths > 0) {
        segments.push(
          this.renderSegment(
            segments.length,
            endPartialBeltIndex,
            endPartialBeltIndex * beltSizeInMonths,
            endPartialBeltSizeInMonths,
            classnames({
              [styles.segmentEntryEnd]: true,
              [styles.segmentEntryLast]: isLastEntryInTimeline,
            }),
            entry
          )
        );
      }

      if (showScale) {
        tickMarkers.push(
          <div
            key={`tick-marker-${tickMarkers.length}`}
            className={styles.scaleTickMarker}
            style={{
              left: `${100.0 * entryOffsetInMonths / careerSizeInMonths}%`,
              height: `${(timelinesCount - timelineIndex) *
                (timelineBreadthInRem + timelineGutterInRem)}rem`,
            }}
          />
        );

        if (isLastEntryInTimeline) {
          tickMarkers.push(
            <div
              key={`tick-marker-${tickMarkers.length}`}
              className={styles.scaleTickMarker}
              style={{
                left: `${100.0 * (entryOffsetInMonths + entrySizeInMonths) / careerSizeInMonths}%`,
                height: `${(timelinesCount - timelineIndex) *
                  (timelineBreadthInRem + timelineGutterInRem)}rem`,
              }}
            />
          );
        }
      }
    });

    return (
      <div
        key={`timeline-${timelineIndex}`}
        className={styles.timeline}
        style={{
          top: `${timelineIndex * (timelineBreadthInRem + timelineGutterInRem)}rem`,
          height: `${timelineBreadthInRem}rem`,
        }}
      >
        <div className={styles.dottedLine} />
        {segments}
        {tickMarkers}
      </div>
    );
  }

  renderScaleTick(tickDate, tickIndex) {
    const { careerStartDate, careerSizeInMonths } = this.props;

    const left = 100.0 * dateDiffInMonths(careerStartDate, tickDate) / careerSizeInMonths;

    return (
      <span
        key={`career-timelines-viz-scale-tick-${tickIndex}`}
        className={styles.scaleTick}
        style={{ left: `${left}%` }}
      >
        {formatEntryDateShortYearShortMonth(ensureDateMonth(tickDate))}
      </span>
    );
  }

  renderScale() {
    const {
      scaleTicks,
      timelinesCount,
      settings: { timelineBreadthInRem, timelineGutterInRem },
    } = this.props;

    return (
      <div
        key="career-timelines-viz-scale"
        className={styles.scale}
        style={{
          top: `${timelinesCount * (timelineBreadthInRem + timelineGutterInRem)}rem`,
        }}
      >
        {scaleTicks.filter(Boolean).map(this.renderScaleTick)}
      </div>
    );
  }

  toggleInfoModal() {
    this.setState({
      infoModalOpen: !this.state.infoModalOpen,
    });
  }

  renderInfoButtonAndModal() {
    return (
      <div style={{ position: 'absolute', top: '-2rem', right: '1.5rem' }}>
        <Button color="link" icon="fa fa-info-circle" onClick={this.toggleInfoModal} />
        <InfoModal isOpen={this.state.infoModalOpen} toggle={this.toggleInfoModal} />
      </div>
    );
  }

  render() {
    const {
      careerStartDate,
      careerEndDate,
      timelines,
      timelinesCount,
      settings: { showScale, timelineBreadthInRem, timelineGutterInRem, showExtremeDates },
    } = this.props;

    if (!timelinesCount) return null;

    return (
      <div className={styles.vizWrapper}>
        {this.renderInfoButtonAndModal()}
        {showExtremeDates && (
          <div className={styles.careerStartDate}>
            {formatEntryDateLongYearShortMonth(ensureDateMonth(careerStartDate))}
          </div>
        )}
        <div
          className={styles.viz}
          style={{
            height: `${(showScale ? 3 : 0) +
              timelinesCount * (timelineBreadthInRem + timelineGutterInRem) -
              (showScale ? 0 : timelineGutterInRem)}rem`,
          }}
        >
          {timelines.map(this.renderTimeline)}
          {showScale && this.renderScale()}
        </div>
        {showExtremeDates && (
          <div className={styles.careerEndDate}>
            {formatEntryDateLongYearShortMonth(
              ensureDateMonth(careerEndDate) || presentDateInYearAndMonth()
            )}
          </div>
        )}
      </div>
    );
  }
}

function dateInTotalMonths(value) {
  if (!value) return undefined;
  return value.year * 12 + (value.month || 0);
}

function dateDiffInMonths(left, right) {
  if (!left || !right) return undefined;
  const leftMonths = dateInTotalMonths(left);
  const rightMonths = dateInTotalMonths(right);
  return rightMonths - leftMonths;
}

function effectiveEndDate(entry) {
  if (!entry) return undefined;

  let endDate = entry.endDate;
  if (entry.currentlyWorking || entry.currentlyStudying || !entry.endDate) {
    const now = new Date();
    endDate = { year: now.getFullYear(), month: now.getMonth() + 1 };
  }
  return endDate;
}

function mapStateToProps(state, ownProps) {
  const { userEducation, userWorkExperience } = ownProps;

  const settings = {
    scaleOnHover: false,
    showScale: matchMedia(`(min-width: ${themeConfig.layout.gridBreakpoints.sm}px)`).matches,
    timelineBreadthInRem: 1,
    timelineGutterInRem: 1,
    showExtremeDates: false,
  };

  const userEducationEntriesHavingStartDate = userEducation
    ? userEducation.filter(entry => entry.startDate)
    : [];
  const educationStartDate =
    userEducationEntriesHavingStartDate[0] && userEducationEntriesHavingStartDate[0].startDate;
  const userWorkExperienceEntriesHavingStartDate = userWorkExperience
    ? userWorkExperience.filter(entry => entry.startDate)
    : [];
  const workExperienceStartDate =
    userWorkExperienceEntriesHavingStartDate[0] &&
    userWorkExperienceEntriesHavingStartDate[0].startDate;
  const lastEducationEntryEndDate =
    userEducation &&
    userEducation.length &&
    effectiveEndDate(userEducation[userEducation.length - 1]);
  const lastWorkExperienceEntryEndDate =
    userWorkExperience &&
    userWorkExperience.length &&
    effectiveEndDate(userWorkExperience[userWorkExperience.length - 1]);
  const careerStartDate = minDateByYearAndMonth(educationStartDate, workExperienceStartDate);
  let careerEndDate = maxDateByYearAndMonth(
    lastEducationEntryEndDate,
    lastWorkExperienceEntryEndDate
  );
  const timelines = [];
  let scaleTicks = [];

  const entries = (userEducation || [])
    .concat(userWorkExperience || [])
    .sort(compareByStartDateThenEndDate);

  entries.forEach(entry => {
    if (!entry || !entry.startDate) return;

    let minimumDiffInMonths = Number.MAX_VALUE;
    let minimumDiffTimelineIndex = null;
    let overlapCount = 0;
    scaleTicks.push(ensureDateMonth(entry.startDate));
    const entryEndDate = effectiveEndDate(entry);
    entry.endDate = entryEndDate;
    careerEndDate = maxDateByYearAndMonth(careerEndDate, entryEndDate);
    timelines.forEach((timeline, timelineIndex) => {
      const lastEntryInTimeline = timeline[timeline.length - 1];
      const lastEndDate = effectiveEndDate(lastEntryInTimeline);
      const nextStartDate = entry.startDate;
      if (nextStartDate && lastEndDate) {
        const diff = dateDiffInMonths(lastEndDate, entry.startDate);
        if (diff < 0) overlapCount++;
        else if (diff < minimumDiffInMonths) {
          minimumDiffInMonths = diff;
          minimumDiffTimelineIndex = timelineIndex;
        }
      }
    });
    if (overlapCount >= timelines.length) {
      // This entry overlaps will all timelines. So create a new one
      timelines.push([entry]);
    } else {
      // This entry is ahead of atleast one timeline
      const serialTimeline = timelines[minimumDiffTimelineIndex];
      if (serialTimeline) timelines[minimumDiffTimelineIndex].push(entry);
    }
  });

  timelines.forEach(timeline => {
    const lastEntryInTimeline = timeline[timeline.length - 1];
    scaleTicks.push(lastEntryInTimeline.endDate);
  });

  const careerSizeInMonths = dateDiffInMonths(careerStartDate, careerEndDate);
  scaleTicks = scaleTicks.filter((tick1, index1, self) => {
    const index2 = self.findIndex(
      tick2 => tick1 && tick2 && tick1.year === tick2.year && tick1.month === tick2.month
    );
    return index1 === index2;
  });

  return {
    settings,
    timelines,
    timelinesCount: (timelines && timelines.length) || 0,
    scaleTicks,
    careerStartDate,
    careerEndDate,
    careerSizeInMonths,
  };
}

export default connect(mapStateToProps)(injectIntl(CareerTimelinesViz));
